"use client";

import { useEffect, useMemo, useState } from "react";
import { ChevronDown, Eye, Minus, Plus, ShoppingBag } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { CartIcon } from "@/components/shop/CartIcon";
import { Link } from "@/components/common/Link";
const categories = [{
  id: "all",
  label: "All"
}, {
  id: "beach",
  label: "Beach Escapes"
}, {
  id: "city",
  label: "City Breaks"
}, {
  id: "adventure",
  label: "Adventure"
}, {
  id: "luxury",
  label: "Luxury"
}, {
  id: "family",
  label: "Family"
}];
const products = [{
  id: 1,
  name: "Mauritius Coastal Bliss",
  category: "beach",
  categoryLabel: "Beach Escapes",
  price: "From R18,450",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1715383/img/mauritius_beach.webp",
  description: "Unwind on the pristine white sands of Mauritius with a luxury resort stay featuring turquoise lagoon views and tropical gardens.",
  sizes: ["5 Nights", "7 Nights", "10 Nights"]
}, {
  id: 2,
  name: "London Heritage Tour",
  category: "city",
  categoryLabel: "City Breaks",
  price: "From R22,300",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1715383/img/london_city.png",
  description: "Explore the historic streets of London, from the West End to the Thames, staying in a centrally located premium hotel.",
  sizes: ["4 Days", "6 Days", "8 Days"]
}, {
  id: 3,
  name: "Victoria Falls Adventure",
  category: "adventure",
  categoryLabel: "Adventure",
  price: "From R9,800",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1715383/img/vic_falls.png",
  description: "Experience the smoke that thunders. This package includes guided falls tours, sunset cruises, and optional bungee jumping.",
  sizes: ["2 Nights", "3 Nights", "4 Nights"]
}, {
  id: 4,
  name: "Dubai Luxury Escape",
  category: "luxury",
  categoryLabel: "Luxury",
  price: "From R28,900",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1715383/img/dubai_luxury.png",
  description: "Indulge in the opulence of Dubai with a stay at a 5-star desert resort, private shopping tours, and fine dining experiences.",
  sizes: ["5 Days", "7 Days"]
}, {
  id: 5,
  name: "Phuket Family Fun",
  category: "family",
  categoryLabel: "Family",
  price: "From R14,200",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1715383/img/phuket_family.png",
  description: "A perfect family getaway in Thailand with kid-friendly resorts, water park access, and cultural island excursions.",
  sizes: ["7 Nights", "10 Nights"]
}, {
  id: 6,
  name: "Cape Town Winelands",
  category: "luxury",
  categoryLabel: "Luxury",
  price: "From R12,500",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1715383/img/cape_winelands.webp",
  description: "Savor the finest South African wines with a boutique stay in Stellenbosch, featuring private cellar tours and gourmet pairings.",
  sizes: ["3 Days", "5 Days"]
}, {
  id: 7,
  name: "Zanzibar Spice Island",
  category: "beach",
  categoryLabel: "Beach Escapes",
  price: "From R16,700",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1715383/img/zanzibar_beach.png",
  description: "Immerse yourself in the exotic charm of Zanzibar. Enjoy dhow cruises, spice farm tours, and relaxing beach days.",
  sizes: ["5 Nights", "7 Nights"]
}, {
  id: 8,
  name: "New York City Lights",
  category: "city",
  categoryLabel: "City Breaks",
  price: "From R34,600",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1715383/img/nyc_lights.png",
  description: "The ultimate Big Apple experience. Includes Broadway tickets, Empire State Building access, and a stay in Midtown.",
  sizes: ["5 Days", "7 Days", "10 Days"]
}, {
  id: 9,
  name: "Kruger Safari Classic",
  category: "adventure",
  categoryLabel: "Adventure",
  price: "From R15,400",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1715383/img/kruger_safari.png",
  description: "Witness the Big Five in their natural habitat with expert-led game drives and authentic bush lodge accommodation.",
  sizes: ["3 Nights", "4 Nights", "5 Nights"]
}, {
  id: 10,
  name: "Rome Eternal City",
  category: "city",
  categoryLabel: "City Breaks",
  price: "From R21,200",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1715383/img/rome_city.png",
  description: "Walk through history in Rome. Visit the Colosseum and Vatican City while staying in a charming boutique hotel.",
  sizes: ["4 Days", "6 Days"]
}, {
  id: 11,
  name: "Maldives Private Atoll",
  category: "luxury",
  categoryLabel: "Luxury",
  price: "From R45,000",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1715383/img/maldives_luxury.png",
  description: "The pinnacle of seclusion. An overwater villa experience with private butler service and world-class diving.",
  sizes: ["7 Nights", "10 Nights"]
}, {
  id: 12,
  name: "Bali Spiritual Retreat",
  category: "adventure",
  categoryLabel: "Adventure",
  price: "From R19,300",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1715383/img/bali_retreat.png",
  description: "Find your zen in Ubud. This package features yoga retreats, temple visits, and lush jungle villa stays.",
  sizes: ["7 Days", "10 Days", "14 Days"]
}];
export default function Getaways() {
  const [activeCategory, setActiveCategory] = useState("all");
  const [sortBy, setSortBy] = useState("featured");
  const [visibleCount, setVisibleCount] = useState(12);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [quickViewId, setQuickViewId] = useState<number | null>(null);
  const [quantity, setQuantity] = useState(1);
  const [selectedSize, setSelectedSize] = useState(0);
  const [scrollProgress, setScrollProgress] = useState(0);
  useEffect(() => {
    const onScroll = () => {
      const h = document.documentElement;
      const total = h.scrollHeight - h.clientHeight;
      const p = total > 0 ? h.scrollTop / total * 100 : 0;
      setScrollProgress(p);
    };
    onScroll();
    window.addEventListener("scroll", onScroll, {
      passive: true
    });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  const filtered = useMemo(() => {
    let list = activeCategory === "all" ? products : products.filter(p => p.category === activeCategory);
    return list;
  }, [activeCategory]);
  const handleFilter = (id: string) => {
    if (id === activeCategory) return;
    setIsTransitioning(true);
    setTimeout(() => {
      setActiveCategory(id);
      setVisibleCount(12);
      setIsTransitioning(false);
    }, 300);
  };
  const visibleProducts = filtered.slice(0, visibleCount);
  const activeProduct = quickViewId !== null ? products.find(p => p.id === quickViewId) : null;
  return <section data-section-id="4847" id="most-popular-getaways" className="dark bg-background text-foreground relative pt-32 md:pt-40 pb-24 md:pb-32">
      <div className="fixed top-0 left-0 right-0 h-[2px] z-50 origin-left" style={{
      background: "var(--secondary)",
      transform: `scaleX(${scrollProgress / 100})`,
      transition: "transform 100ms linear"
    }} aria-hidden="true" />

      <div className="absolute top-8 right-6 md:right-12 z-10">
        <CartIcon variant="ghost" iconSize="size-5" />
      </div>

      <div className="max-w-[1400px] mx-auto px-6 md:px-12 overflow-hidden">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-12 items-end">
          <div className="md:col-span-7">
            <p className="text-[12px] tracking-[0.25em] uppercase mb-8 md:mb-10 font-default" style={{
            color: "var(--primary)"
          }}>
              POPULAR DESTINATIONS
            </p>
            <h1 className="font-default font-light leading-[0.95] tracking-tight text-foreground" style={{
            fontSize: "clamp(3.5rem, 8vw, 8rem)"
          }}>
              Curated{" "}
              <span className="italic font-default font-light">Journeys.</span>
            </h1>
          </div>
          <div className="md:col-span-5 md:pb-4">
            <p className="text-base md:text-[16px] leading-relaxed text-foreground/70 md:text-right font-default">
              Discover our hand-picked selection of holiday packages, designed for travelers seeking the extraordinary.
            </p>
          </div>
        </div>

        <div className="w-full h-px mt-16 md:mt-20" style={{
        background: "var(--secondary)"
      }} aria-hidden="true" />

        <div className="mt-12 md:mt-16 flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6">
          <div className="flex gap-3 overflow-x-auto pb-2 lg:pb-0 lg:flex-wrap -mx-6 px-6 lg:mx-0 lg:px-0 scrollbar-hide">
            {categories.map((cat, i) => {
            const isActive = activeCategory === cat.id;
            return <Button key={cat.id} data-index={i} onClick={() => handleFilter(cat.id)} variant="outline" size="sm" className={`shrink-0 rounded-lg text-[12px] tracking-[0.2em] uppercase h-auto px-5 py-2.5 transition-colors duration-300 font-default ${isActive ? "bg-secondary text-white border-secondary" : "bg-transparent text-foreground border-foreground/30 hover:border-foreground/70"}`}>
                  {cat.label}
                </Button>;
          })}
          </div>

          <div className="flex items-center gap-6 shrink-0">
            <span className="text-[12px] tracking-[0.2em] uppercase text-foreground/60 font-default">
              Showing {Math.min(visibleCount, filtered.length)} of {filtered.length}
            </span>
            <Select value={sortBy} onValueChange={setSortBy}>
              <SelectTrigger className="w-[180px] border-foreground/30 bg-transparent text-[12px] tracking-[0.15em] uppercase h-10 font-default">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-popover border-border">
                <SelectItem value="featured" className="text-[12px] tracking-[0.15em] uppercase font-default">
                  Sort: Featured
                </SelectItem>
                <SelectItem value="recent" className="text-[12px] tracking-[0.15em] uppercase font-default">
                  Sort: Recent
                </SelectItem>
                <SelectItem value="price" className="text-[12px] tracking-[0.15em] uppercase font-default">
                  Sort: Price
                </SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className={`mt-16 md:mt-20 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-8 md:gap-x-8 md:gap-y-16 transition-opacity duration-300 ${isTransitioning ? "opacity-0" : "opacity-100"}`}>
          {visibleProducts.map((product, i) => <div key={product.id} data-index={i} className="group" style={{
          transition: "opacity 600ms ease-out, transform 600ms ease-out",
          transitionDelay: isTransitioning ? "0ms" : `${i * 80}ms`
        }}>
              <div className="relative overflow-hidden aspect-[4/5] bg-muted rounded-lg shadow-md">
                <Link to="/ShopPage" className="block w-full h-full">
                  <img src={product.image} data-wvc-srcset={`${product.image}-wvc-srcset`} data-wvc-sizes={`${product.image}-wvc-sizes`} alt={product.name} loading="lazy" decoding="async" className="w-full h-full object-cover transition-transform duration-[800ms] ease-out group-hover:scale-[1.04]" />
                </Link>
                <div className="absolute inset-x-0 bottom-0 translate-y-10 opacity-0 group-hover:translate-y-0 group-hover:opacity-100 transition-all duration-500 ease-out p-4">
                  <Button onClick={() => {
                setQuickViewId(product.id);
                setQuantity(1);
                setSelectedSize(0);
              }} className="w-full bg-secondary text-white hover:bg-secondary/90 h-12 text-[12px] tracking-[0.25em] uppercase font-default rounded-lg">
                    <Eye className="size-4 mr-2" />
                    View Details
                  </Button>
                </div>
              </div>

              <div className="pt-6">
                <p className="text-[11px] tracking-[0.25em] uppercase mb-3 font-default" style={{
              color: "var(--primary)"
            }}>
                  {product.categoryLabel}
                </p>
                <Link to="/ShopPage" className="block">
                  <h3 className="font-default text-[20px] md:text-[22px] leading-tight text-foreground hover:text-foreground/70 transition-colors">
                    {product.name}
                  </h3>
                </Link>
                <div className="w-8 h-px my-3" style={{
              background: "var(--secondary)"
            }} aria-hidden="true" />
                <p className="text-[14px] text-foreground/80 font-default !italic">{product.price}</p>
              </div>
            </div>)}
        </div>

        {visibleCount < filtered.length && <div className="mt-20 md:mt-24 flex justify-center">
            <Button variant="outline" onClick={() => setVisibleCount(c => c + 6)} className="border-secondary bg-transparent text-secondary hover:bg-secondary hover:text-white h-12 px-10 text-[12px] tracking-[0.25em] uppercase font-default rounded-lg">
              Load More
              <ChevronDown className="size-4 ml-2" />
            </Button>
          </div>}
      </div>

      <Dialog open={quickViewId !== null} onOpenChange={o => !o && setQuickViewId(null)}>
        <DialogContent className="max-w-[1100px] w-[95vw] p-0 bg-background border-0 overflow-hidden max-h-[90vh] overflow-y-auto rounded-lg" style={{
        boxShadow: "0 0 0 1px var(--secondary)"
      }}>
          <DialogTitle className="sr-only">{activeProduct?.name}</DialogTitle>
          {activeProduct && <div className="grid grid-cols-1 md:grid-cols-2">
              <div className="bg-muted aspect-[4/5] md:aspect-auto md:min-h-[600px]">
                <img src={activeProduct.image} data-wvc-srcset={`${activeProduct.image}-wvc-srcset`} data-wvc-sizes={`${activeProduct.image}-wvc-sizes`} alt={activeProduct.name} loading="lazy" className="w-full h-full object-cover" />
              </div>

              
            </div>}
        </DialogContent>
      </Dialog>
    </section>;
}